/**
 * Mailbox local-part naming for cold-email carousels.
 *
 * Shared between the provisioning wizard's preview and the provider adapter
 * that actually creates the mailboxes. These MUST agree: if they drift, the
 * wizard shows the operator addresses they will not get, and its
 * duplicate-address warning points at the wrong pair (or misses a real
 * collision, which the provider then rejects mid-order).
 */

/** Friendly local-parts used when the operator does not supply a name. */
export const MAILBOX_LOCAL_PART_FALLBACKS = [
  "outreach",
  "hello",
  "team",
  "hi",
  "contact",
  "sales",
  "connect",
] as const;

export const MAILBOX_LOCAL_PART_MAX_LENGTH = 64;

/**
 * Normalize an operator-typed local-part to something a mail provider accepts:
 * lowercase alphanumerics plus dot/dash/underscore, never leading or trailing
 * punctuation. Returns null when nothing usable survives, so callers fall back
 * to a built-in name rather than trying to create `@domain.com`.
 */
export function sanitizeMailboxLocalPart(raw: string | null | undefined): string | null {
  const cleaned = (raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, "")
    .replace(/^[._-]+|[._-]+$/g, "")
    .slice(0, MAILBOX_LOCAL_PART_MAX_LENGTH);
  return cleaned.length > 0 ? cleaned : null;
}

/** The fallback name for the Nth mailbox *within a single domain*. */
export function fallbackLocalPartAt(indexWithinDomain: number): string {
  const list = MAILBOX_LOCAL_PART_FALLBACKS;
  return indexWithinDomain < list.length
    ? list[indexWithinDomain]
    : `${list[indexWithinDomain % list.length]}${Math.floor(indexWithinDomain / list.length) + 1}`;
}

/** Round-robin spread of `total` mailboxes across `bucketCount` domains. */
export function distributeMailboxes(total: number, bucketCount: number): number[] {
  const out = new Array(Math.max(0, bucketCount)).fill(0);
  if (out.length === 0) return out;
  for (let i = 0; i < Math.max(0, total); i++) out[i % out.length]++;
  return out;
}

export interface PlannedMailbox {
  domain: string;
  /** What this slot would be called if the operator names nothing. */
  fallbackLocalPart: string;
  /** The resolved local-part (operator name if usable, else the fallback). */
  localPart: string;
  address: string;
}

/**
 * Resolve the exact mailbox addresses an order will create.
 *
 * Operator-supplied names are consumed FLAT across the whole order rather than
 * restarting per domain, because the wizard presents one flat list of
 * mailboxes; restarting per domain would silently reassign every name the
 * moment the domain count changed.
 */
export function planMailboxAddresses(opts: {
  domainNames: string[];
  mailboxesCount: number;
  localParts?: (string | null | undefined)[];
}): PlannedMailbox[] {
  const { domainNames, mailboxesCount } = opts;
  const perDomain = distributeMailboxes(mailboxesCount, domainNames.length);
  const supplied = (opts.localParts ?? []).map((p) => sanitizeMailboxLocalPart(p));
  const planned: PlannedMailbox[] = [];
  let cursor = 0;
  domainNames.forEach((domain, di) => {
    for (let j = 0; j < perDomain[di]; j++) {
      const fallbackLocalPart = fallbackLocalPartAt(j);
      const localPart = supplied[cursor] ?? fallbackLocalPart;
      cursor++;
      planned.push({
        domain,
        fallbackLocalPart,
        localPart,
        address: `${localPart}@${domain}`,
      });
    }
  });
  return planned;
}

/** First address that appears twice in a plan, or null when all are unique. */
export function findDuplicateMailboxAddress(planned: PlannedMailbox[]): string | null {
  const seen = new Set<string>();
  for (const p of planned) {
    if (seen.has(p.address)) return p.address;
    seen.add(p.address);
  }
  return null;
}
